import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { finalize, takeUntil, tap, type Subscription } from 'rxjs'
import { show } from '@schmancy/overlay/overlay.service'
import { inExemptLayer } from '@schmancy/overlay/overlay.stack'
import type { ResolvedShowOptions } from '@schmancy/overlay/overlay.types'
import { dragOffset } from '../dock/drag-offset'
import '../surface/surface'

/**
 * Launcher that delegates its expanded panel to the `show()` overlay service.
 * Owns no geometry — renders in normal flow; the consumer positions it.
 */
@customElement('schmancy-boat')
export default class SchmancyBoat extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
			}
		`,
	]

	/** Persistence key — the sessionStorage entry is `schmancy-boat-{id}`; the drag itself is `schmancy-dock`'s. */
	@property({ type: String }) id: string = 'default'

	@property({ type: Boolean, reflect: true }) open: boolean = false

	@queryAssignedElements() private slotted!: Element[]

	/** Opt-in drag: grip, pointer-drag, sessionStorage persist, resize re-clamp.
	 *  False (default) is fully static — no grip, transform, or resize listener. */
	@property({ type: Boolean }) draggable = false

	@state() private isDragging: boolean = false

	#openSource: 'gesture' | 'programmatic' = 'programmatic'

	private ready = false
	private sub?: Subscription
	private captured: Element[] = []
	private wrapper?: HTMLElement

	private openOverlay() {
		if (this.sub) return
		this.wrapper = document.createElement('div')
		this.wrapper.className = 'flex flex-col'
		this.wrapper.toggleAttribute('data-overlay-exempt', inExemptLayer(this))
		this.captured = [...this.slotted]
		this.captured.forEach(node => this.wrapper!.appendChild(node))

		const opts: ResolvedShowOptions = { historyStrategy: 'silent' }
		if (this.#openSource === 'programmatic') {
			const rect = this.getBoundingClientRect()
			opts.anchor = { getBoundingClientRect: () => this.getBoundingClientRect() }
			opts.offset = -rect.height // panel covers the pill, then grows away
		}
		this.#openSource = 'programmatic'

		this.sub = show(this.wrapper, opts)
			.pipe(
				finalize(() => this.restoreSlotted()),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.dispatchScopedEvent('toggle', 'open')
	}

	private restoreSlotted() {
		this.captured.forEach(node => this.appendChild(node))
		this.captured = []
		this.wrapper = undefined
		this.sub = undefined
		if (this.open) this.open = false
		this.dispatchScopedEvent('toggle', 'closed')
	}

	connectedCallback() {
		super.connectedCallback()
		// `draggable` also sets the native HTML5 draggable attribute, which would
		// start a ghost-image drag on pointerdown and race our pointer pipeline.
		this.addEventListener('dragstart', e => e.preventDefault())
	}

	firstUpdated() {
		this.ready = true

		// Static boats must never apply a stored offset or subscribe to resize.
		const handleWrapper = this.draggable ? this.shadowRoot!.querySelector<HTMLElement>('.drag-handle-wrapper') : null
		if (handleWrapper)
			dragOffset(this, handleWrapper, `schmancy-boat-${this.id}`)
				.pipe(
					tap(phase => {
						this.isDragging = phase.dragging
						// A no-move press on the grip is a tap, and the grip doubles as a launcher.
						if (phase.dragging === false && !phase.moved) {
							this.#openSource = 'gesture'
							this.toggle()
						}
					}),
					takeUntil(this.disconnecting),
				)
				.subscribe()

		if (this.open) this.openOverlay()
	}

	protected willUpdate(changed: PropertyValues<this>) {
		super.willUpdate(changed)
		if (!this.ready || !changed.has('open')) return
		if (this.open && !this.sub) this.openOverlay()
		else if (!this.open && this.sub) {
			this.#openSource = 'programmatic'
			this.sub.unsubscribe()
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.sub?.unsubscribe()
	}

	toggle() {
		this.open = !this.open
	}

	// A gesture-triggered open anchors the overlay at the ambient gesture, not
	// the boat's rect — distinguishes this from the programmatic open path.
	private onTriggerClick = () => {
		this.#openSource = 'gesture'
		this.toggle()
	}

	private onTriggerKeydown = (e: KeyboardEvent) => {
		if (e.key !== 'Enter' && e.key !== ' ') return
		e.preventDefault()
		this.#openSource = 'gesture'
		this.toggle()
	}

	// A parent re-render re-asserting default-slot children fires slotchange;
	// re-relocate them into the live overlay wrapper to stop the DOM ping-pong.
	private onDefaultSlotChange = () => {
		if (!this.sub || !this.wrapper) return
		this.slotted.forEach(node => {
			if (node.parentElement !== this.wrapper) {
				this.captured.push(node)
				this.wrapper!.appendChild(node)
			}
		})
	}

	protected render(): unknown {
		return html`
			<!-- This box holds only slots, so what paints in it is the consumer's own
			     composition, and a trigger's badge swept off its corner belongs outside
			     the pill. The fill, hairline and shadow stay inside the radius either
			     way — overflow reaches descendants, never the box declaring it. -->
			<schmancy-surface
				type="container"
				class=${this.classMap({ 'inline-flex rounded-2xl shadow-md': true, 'opacity-90': this.isDragging, 'scale-[0.98]': this.isDragging })}
			>
				<div
					class="flex items-center cursor-pointer"
					role="button"
					tabindex="0"
					aria-label=${this.ariaLabel || 'Open panel'}
					aria-expanded=${this.open}
					title="Click to open"
					@click=${this.onTriggerClick}
					@keydown=${this.onTriggerKeydown}
				>
					<slot name="trigger"></slot>
				</div>

				${
					this.draggable
						? html`<div
								class="drag-handle-wrapper flex items-center cursor-grab touch-none select-none px-1 text-surface-on-variant"
							>
								<slot name="drag-handle">⠿</slot>
							</div>`
						: ''
				}

				<!-- Parks default-slot content while collapsed; relocated into show() on open. -->
				<div hidden><slot @slotchange=${this.onDefaultSlotChange}></slot></div>
			</schmancy-surface>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-boat': SchmancyBoat
	}
}
